import { useMemo } from 'react'
import { Trans, useTranslation } from 'next-i18next'

import { useGetProducts } from '~/booking/hooks/useGetProduct'
import { Link } from '~/components/Link'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import {
  getPriceWithCurrency,
  i18nifyHighlights,
} from '~/shared/components/TutorOnDemandProductCards/TutorOnDemandProductCards.utils'
import ROUTES from '~/shared/constants/routes.const'

export default function Hero({ className = '' }): React.ReactElement {
  const { t } = useTranslation('tutorOnDemand')
  const { data: bookingProducts } = useGetProducts()
  const style = {
    backgroundImage: `linear-gradient(111.67deg, #7D38D6 25.12%, #58C1FC 100%)`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 100%',
  }

  const productPrices = useMemo(() => {
    if (bookingProducts) {
      const { lowestHourlyPriceProduct } = bookingProducts
      if (!lowestHourlyPriceProduct) return
      const lowestHourlyPriceLabel = getPriceWithCurrency(
        lowestHourlyPriceProduct.currency,
        lowestHourlyPriceProduct.priceHourly,
      )
      return lowestHourlyPriceLabel
    }
  }, [bookingProducts])

  const highlights = i18nifyHighlights(t, 'tutoring')
  const price = t('tutorOnDemand:landingPage.productCard.privateTutor.price', {
    price: productPrices ?? ' - ',
  })

  return (
    <div className={`${className}`} style={style}>
      <div className=" max-w-339 mx-auto">
        <header className="pt-3 pb-6 md:mt-4 md:mb-6 lg:mb-14 text-white text-center">
          <h1 className="md:mb-2 text-8 md:text-11 lg:text-15 leading-9 md:leading-12 lg:leading-21 font-bold md:font-extrabold tracking-px">
            {t('landingPage.whatIsTutorOnDemand.title')}
          </h1>
          <p className="max-w-4xl text-xl md:text-2xl leading-8 mx-auto font-light">
            {Trans({
              t,
              i18nKey: 'landingPage.chanceOfBetterGrade',
              components: { strong: <strong className="font-semibold" /> },
              children: t('landingPage.chanceOfBetterGrade'),
              values: { value: 92 },
            })}
          </p>
        </header>

        <div className="md:pl-10 lg:pl-44 w-full text-white ">
          <h3 className="text-md leading-9 md:text-2xl lg:text-3xl font-semibold tracking-wider pb-4 text-center md:text-left">
            {t('landingPage.heroTitle')}
          </h3>
          <div className="flex flex-col md:flex-row w-full justify-between">
            <div className="min-w-max text-center">
              <ul className=" inline-block list-disc space-y-4 md:space-y-2 text-md md:text-5 lg:text-2xl leading-6 md:leading-9 tracking-wider pl-7 text-left">
                {highlights.map((elem) => (
                  <li key={elem}>
                    <span>{elem}</span>
                  </li>
                ))}
              </ul>
              <h3 className="text-md md:text-2xl lg:text-3xl leading-6 font-semibold tracking-wider py-7 text-center md:text-left">
                {price}
              </h3>
              <div className="flex justify-center md:justify-start py-2 pb-12 md:pb-25">
                <Link
                  className="bg-orange ring-2 ring-black text-black text-2xl rounded-2.5 px-25 py-1 hover:bg-purple-dark hover:ring-purple-dark hover:text-white focus:ring-8 transition-colors duration-300 ease-in-out"
                  to={ROUTES.BOOKING_FOR_SELF}
                >
                  {t('generics:book')}
                </Link>
              </div>
            </div>
            <ResponsiveImage
              alt="math vault sitemap"
              className="self-end px-2"
              height={405}
              imageClassName=""
              srcKey="mentor-on-demand/tutor-on-demand-hero.png"
              width={683}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
